import React, { useEffect, useRef } from "react";
import SignaturePadLib from "signature_pad";

// Crop a canvas to the bounding box of its non-transparent pixels, with a
// small padding, and return the cropped PNG as a data URL. Used so the
// signature fills the rendered PDF field rather than a small patch of an
// otherwise-empty full-screen canvas.
function trimTransparent(canvas) {
  const ctx = canvas.getContext("2d");
  const { width, height } = canvas;
  const { data } = ctx.getImageData(0, 0, width, height);
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (data[(y * width + x) * 4 + 3] > 0) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return canvas.toDataURL("image/png");
  const pad = Math.max(8, Math.round(Math.max(width, height) * 0.02));
  minX = Math.max(0, minX - pad);
  minY = Math.max(0, minY - pad);
  maxX = Math.min(width - 1, maxX + pad);
  maxY = Math.min(height - 1, maxY + pad);
  const w = maxX - minX + 1;
  const h = maxY - minY + 1;
  const out = document.createElement("canvas");
  out.width = w;
  out.height = h;
  out.getContext("2d").drawImage(canvas, minX, minY, w, h, 0, 0, w, h);
  return out.toDataURL("image/png");
}

// Full-screen signature canvas with touch support. Calls onAccept with a
// trimmed PNG data URL of the drawn stroke.
export default function SignaturePad({ open, onCancel, onAccept }) {
  const canvasRef = useRef(null);
  const padRef = useRef(null);

  useEffect(() => {
    if (!open || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const resize = () => {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext("2d").scale(ratio, ratio);
      padRef.current?.clear();
    };
    padRef.current = new SignaturePadLib(canvas, {
      minWidth: 2.0,
      maxWidth: 4.4,
      penColor: "#0f172a",
      backgroundColor: "rgba(255,255,255,0)"
    });
    resize();
    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
      padRef.current?.off();
      padRef.current = null;
    };
  }, [open]);

  if (!open) return null;

  const clear = () => padRef.current?.clear();
  const accept = () => {
    const pad = padRef.current;
    if (!pad || pad.isEmpty()) return;
    onAccept(trimTransparent(canvasRef.current));
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      <div className="flex items-center justify-between border-b border-slate-200 p-3">
        <span className="font-semibold">Draw your signature</span>
        <button className="btn-secondary text-sm" onClick={onCancel}>
          Cancel
        </button>
      </div>
      <div className="flex-1 p-3">
        <canvas
          ref={canvasRef}
          className="h-full w-full touch-none rounded-lg border border-dashed border-slate-300"
        />
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-slate-200 p-3">
        <button className="btn-secondary" onClick={clear}>
          Clear
        </button>
        <button className="btn-primary" onClick={accept}>
          Accept signature
        </button>
      </div>
    </div>
  );
}
